import { useTranslation } from "react-i18next";
import { UserRole } from "@/shared/types/user-role";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";

interface Workspace {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
}

interface User {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  status: string;
  role?: UserRole;
  products?: Workspace[];
  productsLoading?: boolean;
}

interface OrganizationUsersTableProps {
  users: User[];
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onUnlinkUser?: (user: User) => void;
  currentUserId?: string;
}

export const OrganizationUsersTable = ({
  users,
  onEditUser,
  onDeleteUser,
  onUnlinkUser,
  currentUserId
}: OrganizationUsersTableProps) => {
  const { t } = useTranslation();

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination({
    initialPage: 1,
    initialPageSize: 10
  });

  const paginatedUsers = paginateData(users);

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name.split(' ').map(word => word[0]?.toUpperCase()).join('').slice(0, 2) || '?';
    }
    // Se não tiver nome, use a primeira letra do email
    return email[0]?.toUpperCase() || '?';
  };

  const getRoleLabel = (role: string | UserRole | undefined) => {
    if (!role) return t("settings.users.roles.user");

    switch (role.toLowerCase()) {
      case 'superadmin':
        return t("settings.users.roles.superadmin");
      case 'admin':
        return t("settings.users.roles.admin");
      case 'user':
      default:
        return t("settings.users.roles.user");
    }
  };

  // Determinar se o usuário pode ser excluído (não pode excluir a si mesmo)
  const canDelete = (userId: string) => userId !== currentUserId;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12"></TableHead>
            <TableHead>{t("settings.users.fields.name")}</TableHead>
            <TableHead>{t("settings.users.fields.email")}</TableHead>
            <TableHead>{t("settings.users.fields.role")}</TableHead>
            <TableHead>{t("settings.users.fields.workspaces")}</TableHead>
            <TableHead className="w-[100px] text-right">{t("common.labels.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedUsers.map((user) => (
          <TableRow key={user.id} className={user.role === UserRole.SUPERADMIN ? "bg-blue-50/40 dark:bg-blue-950/20" : undefined}>
            <TableCell>
              <Avatar>
                <AvatarImage src={user.avatar_url || undefined} alt={user.full_name || user.email} />
                <AvatarFallback>
                  {getInitials(user.full_name, user.email)}
                </AvatarFallback>
              </Avatar>
            </TableCell>
            <TableCell className="font-medium">
              {user.full_name || t("settings.users.noName")}
            </TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>
              <Badge variant="outline">{getRoleLabel(user.role)}</Badge>
            </TableCell>
            <TableCell>
              {user.role === UserRole.SUPERADMIN ? (
                <span className="text-xs text-muted-foreground italic" title={t("settings.users.superadminAllWorkspaces")}>{t("common.all", "All")}</span>
              ) : user.productsLoading ? (
                <div className="space-y-1">
                  <Skeleton className="h-8 w-20" />
                </div>
              ) : user.products && user.products.length > 0 ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center">
                        {user.products.slice(0, 5).map((workspace: Workspace, index: number) => (
                          <div 
                            key={workspace.id} 
                            className="relative rounded-full bg-background border border-border overflow-hidden shadow-sm"
                            style={{ 
                              width: '1.5rem', 
                              height: '1.5rem',
                              marginLeft: index === 0 ? '0' : '-0.35rem',
                              zIndex: index, 
                              boxShadow: '0 0 0 1.5px var(--background)'
                            }}
                          >
                            {workspace.logo_url ? (
                              <img 
                                src={workspace.logo_url} 
                                alt={workspace.name} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full bg-primary/10 text-xs font-semibold">
                                {workspace.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                        ))}
                        {user.products.length > 5 && (
                          <div 
                            className="relative flex items-center justify-center rounded-full bg-muted text-xs font-medium shadow-sm"
                            style={{ 
                              width: '1.5rem', 
                              height: '1.5rem',
                              marginLeft: '-0.35rem',
                              zIndex: 5 
                            }}
                          >
                            <span className="text-[10px]">+{user.products.length - 5}</span>
                          </div>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="max-w-[200px]">
                        <p className="font-semibold mb-1">{t("settings.users.workspacesList")}</p>
                        <ul className="text-xs space-y-1">
                          {user.products.map((workspace: Workspace) => (
                            <li key={workspace.id} className="truncate">{workspace.name}</li>
                          ))}
                        </ul>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {t("settings.users.noWorkspaces")}
                </span>
              )}
            </TableCell>
            <TableCell className="text-right flex justify-end items-center space-x-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={(e) => {
                        console.log("[DEBUG-BUTTON] Edit button clicked for user:", user);
                        e.stopPropagation(); // Evitar propagação do evento
                        
                        // Sempre chamar onEditUser, independentemente de haver produtos ou não
                        onEditUser(user);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">{t("common.edit")}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("settings.users.editUserWorkspacesButton")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onUnlinkUser ? onUnlinkUser(user) : onDeleteUser(user)}
                      disabled={!canDelete(user.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">{t("common.delete")}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {canDelete(user.id)
                      ? onUnlinkUser
                        ? t("settings.users.unlinkUserButton")
                        : t("settings.users.deleteUserButton")
                      : t("settings.users.cannotDeleteSelf")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableCell>
          </TableRow>
          ))}
        </TableBody>
      </Table>

      {users && users.length > 0 && (
        <DataPagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={users.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 20, 50]}
        />
      )}
    </>
  );
};
